import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  DateRangeModule,
  MultiViewCalendarComponent,
  MultiViewCalendarModule,
  SelectionRange,
  SelectionRangeEnd,
} from '@progress/kendo-angular-dateinputs';
import { TextBoxModule } from '@progress/kendo-angular-inputs';
import { formatDate } from '@progress/kendo-angular-intl';
import { PopupModule } from '@progress/kendo-angular-popup';
import { Day } from '@progress/kendo-date-math';
import { DeprecatedQPointIconButtonComponent, QPointButtonComponent } from '@qpoint/buttons';
import { QPointDateFormat, QPointDateFormatPipe } from '@qpoint/i18n';
import { add, differenceInDays, Duration, isSameDay, sub } from 'date-fns';
import { isDate } from 'lodash';

export interface QPointMultiViewDateRangePickerValue extends SelectionRange {
  start: Date | null;
  end: Date | null;
}

export interface QPointMultiViewDateRangePickerConfig {
  single: Duration;
  range: Duration & { rangeDelta: boolean };
}

export type QPointMultiViewDateRangePickerSelectionMode = 'single' | 'range';

export interface QPointMultiViewDateRangePickerTranslations {
  from: string;
  to: string;
  modeSingle: string;
  modeRange: string;
}

export const QPointMultiViewDateRangePickerTranslationsDefault: QPointMultiViewDateRangePickerTranslations = {
  from: 'common.multiViewDateRangePicker.from',
  to: 'common.multiViewDateRangePicker.to',
  modeSingle: 'common.multiViewDateRangePicker.modeSingle',
  modeRange: 'common.multiViewDateRangePicker.modeRange',
};

@Component({
    selector: 'qpoint-multi-view-date-range-picker',
    templateUrl: './multi-view-date-range-picker.component.html',
    styleUrls: ['./multi-view-date-range-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QPointMultiViewDateRangePickerComponent),
            multi: true,
        }
    ],
    imports: [
        TextBoxModule,
        QPointButtonComponent,
        PopupModule,
        TranslateModule,
        QPointDateFormatPipe,
        DeprecatedQPointIconButtonComponent,
        MultiViewCalendarModule,
        DateRangeModule,
        CommonModule
    ]
})
export class QPointMultiViewDateRangePickerComponent implements ControlValueAccessor {
  private _value: WritableSignal<QPointMultiViewDateRangePickerValue> = signal({start: null, end: null});
  showPopup = false;
  today = new Date();

  isPrevDisabled = computed(() => {
    if (!this.disabledDates || !this._value().start || !this._value().end) {
      return false;
    }

    if (this.mode === 'single') {
      return this.isDateDisabled(sub(this._value().start, this.incrementConfig.single));
    } else {
      return this.isDateDisabled(sub(this._value().start, this.incrementConfig.range));
    }
  });

  isNextDisabled = computed(() => {
    if (!this.disabledDates || !this._value().start || !this._value().end) {
      return false;
    }

    if (this.mode === 'single') {
      return this.isDateDisabled(add(this._value().end, this.incrementConfig.single));
    } else {
      return this.isDateDisabled(add(this._value().end, this.incrementConfig.range));
    }
  });

  @Input() value: QPointMultiViewDateRangePickerValue = {start: null, end: null};
  @Input() views = 2;
  @Input() disabled = false;
  @Input() showViewHeader = true;
  @Input() weekNumber = true;
  @Input() mode: QPointMultiViewDateRangePickerSelectionMode = 'single';
  @Input() showModeSelection = true;
  @Input() showNavigationArrows = false;
  @Input() autoClose = false;
  @Input() disabledDatesRangeValidation = false;
  @Input() showCalendarButton = true;
  @Input() placeholder: string;
  @Input() showClearButton = false;

  @Input() translations: QPointMultiViewDateRangePickerTranslations = QPointMultiViewDateRangePickerTranslationsDefault;

  @Input() incrementConfig: QPointMultiViewDateRangePickerConfig = {
    single: {
      days: 1,
    },
    range: {
      weeks: 1,
      rangeDelta: false,
    }
  };

  @Input() disabledDates: ((date: Date) => boolean) | Date[] | Day[] | undefined = undefined;

  @Output() valueChanged = new EventEmitter<QPointMultiViewDateRangePickerValue>();

  private currentSelectionRange: SelectionRangeEnd = 'start';

  @ViewChild('anchor', {read: ElementRef}) public anchor: ElementRef;
  @ViewChild('popup', {read: ElementRef}) public popup: ElementRef;
  @ViewChild(MultiViewCalendarComponent) calendar: MultiViewCalendarComponent;

  @HostListener('document:keydown', ['$event'])
  public keydown(event: KeyboardEvent): void {
    if (event.code === 'Escape') {
      this.togglePopup(false);
    }
  }

  @HostListener('document:click', ['$event'])
  public documentClick(event: KeyboardEvent): void {
    if (!this.contains(event.target)) {
      this.togglePopup(false);
    }
  }

  constructor(private cdr: ChangeDetectorRef) {
  }

  get valueAsString(): string {
    if (this.mode === 'single' && this.value?.start) {
      return this.formatDate(this.value.start);
    } else if (this.mode === 'range' && this.value?.start && this.value?.end) {
      return `${this.formatDate(this.value.start)} - ${this.formatDate(this.value.end)}`;
    } else {
      return '';
    }
  }

  public get format(): string {
    return QPointDateFormat.DayOfWeekWithShortDate;
  }


  public onChange = (_: QPointMultiViewDateRangePickerValue) => {
  };
  public onTouched = () => {
  };


  writeValue(obj: QPointMultiViewDateRangePickerValue): void {
    if (obj == null) {
      this.value = {start: null, end: null};
    } else {
      this.value = obj;

      if ((obj.start && obj.end == null) || isSameDay(obj.start, obj.end)) {
        this.mode = this.mode || 'single';
        this.value = {start: obj.start, end: obj.start};
      } else if (obj.start && obj.end && !isSameDay(obj.start, obj.end)) {
        this.mode = 'range';
      }

      this.onChange(this.value);
    }

    this._value.set(this.value);

    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  togglePopup(show?: boolean): void {
    this.showPopup = show !== undefined ? show : !this.showPopup;
  }

  selectionRangeChanged(event: SelectionRange) {
    if (this.mode === 'single') {
      const value = {start: event[this.currentSelectionRange], end: event[this.currentSelectionRange]};
      this.value = {...value};
      this.valueChanged.emit({...value});
      this.onChange(value);
    } else {
      this.value = event;
      this.valueChanged.emit(event);
      this.onChange(event);
    }

    this._value.set(this.value);
    if (this.autoClose) {
      this.togglePopup(false);
    }
  }

  activeRangeEndChanged(event: SelectionRangeEnd) {
    if (event != null) {
      this.currentSelectionRange = event;
    }
  }

  changeSelectionMode(mode: QPointMultiViewDateRangePickerSelectionMode) {
    this.mode = mode;

    if (mode === 'single' && this.value.start != null) {
      this.selectionRangeChanged({start: this.value.start, end: this.value.start});
    }
  }

  incrementValue() {
    if (!this.value || this.disabled) {
      return;
    }

    if (this.mode === 'single' && this.value.start) {
      const start = add(this.value.start, this.incrementConfig.single);
      this.selectionRangeChanged({start, end: start});
    } else if (this.mode === 'range' && this.value.start && this.value.end) {
      let start = add(this.value.start, this.incrementConfig.range);
      let end = add(this.value.end, this.incrementConfig.range);

      if (this.incrementConfig.range.rangeDelta) {
        const deltaInDays = differenceInDays(this.value.end, this.value.start);
        start = add(this.value.start, {days: deltaInDays});
        end = add(this.value.end, {days: deltaInDays});
      }

      this.selectionRangeChanged({start, end});
    }
  }

  decrementValue() {
    if (!this.value) {
      return;
    }

    if (this.mode === 'single' && this.value.start) {
      const start = sub(this.value.start, this.incrementConfig.single);
      this.selectionRangeChanged({start, end: start});
    } else if (this.mode === 'range' && this.value.start && this.value.end) {
      let start = sub(this.value.start, this.incrementConfig.range);
      let end = sub(this.value.end, this.incrementConfig.range);
      if (this.incrementConfig.range.rangeDelta) {
        const deltaInDays = differenceInDays(this.value.end, this.value.start);
        start = sub(this.value.start, {days: deltaInDays});
        end = sub(this.value.end, {days: deltaInDays});
      }
      this.selectionRangeChanged({start, end});
    }
  }

  private contains(target: EventTarget): boolean {
    return (
      this.anchor.nativeElement.contains(target) ||
      (this.popup ? this.popup.nativeElement.contains(target) : false)
    );
  }

  private formatDate(date: Date): string {
    return formatDate(date, 'dd.MM.y');
  }

  calendarPrev() {
    this.calendar.keydown({
      ctrlKey: true,
      keyCode: 37,
      altKey: false,
      preventDefault: () => {
      }
    });
  }

  calendarNext() {
    this.calendar.keydown({
      ctrlKey: true,
      keyCode: 39,
      altKey: false,
      preventDefault: () => {
      }
    });
  }

  calendarToday() {
    this.today = new Date();
    this.selectionRangeChanged({start: this.today, end: this.today});
  }

  private isDateDisabled(date: Date): boolean {
    if (!this.disabledDates) {
      return false;
    }

    if (typeof this.disabledDates === 'function') {
      return this.disabledDates(date);
    }

    if (Array.isArray(this.disabledDates) && this.disabledDates.length > 0) {
      const disabledDates = this.disabledDates as (Date[] | Day[]);

      if ((disabledDates as Date[]).every(d => isDate(d))) {
        return disabledDates.some(d => isSameDay(d, date));
      }

      if ((disabledDates as Day[]).every(d => Object.values(Day).includes(d))) {
        return disabledDates.some(d => date.getDay() === d);
      }
    }

    return false;
  }

  resetValue($event: Event) {
    this.writeValue({start: null, end: null});
    $event.stopPropagation();
    $event.preventDefault();
  }
}
